import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ShieldCheck, KeyRound, Mail, Lock, Check, AlertTriangle, ChevronDown, ChevronRight, FileText, FolderArchive, ClipboardList, BookCheck, Receipt, Sparkles } from "lucide-react";

/**
 * iter-63: Vault Settings — per-section passcode management.
 *
 * Each section (Records, Staff Docs & Training, Licensing File, Compliance,
 * Playground Inspections, Billing) has its OWN passcode now. Each row on
 * this page exposes three controls for that section:
 *    - Change passcode (current + new + confirm)
 *    - Email a one-time reset link
 *    - Lock now (ends the 30-min unlock session for that section only)
 *
 * Owner-only. Non-owners get 403 from the API.
 */

const SECTION_ICONS = {
  records:    FolderArchive,
  staff_docs: ClipboardList,
  licensing:  BookCheck,
  compliance: FileText,
  playground: Sparkles,
  billing:    Receipt,
};

export default function VaultSettings() {
  return (
    <div data-testid="vault-settings-page" className="space-y-6 max-w-3xl">
      <header>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-700">Security</div>
        <h1 className="mt-2 font-display text-3xl font-medium text-stone-900">Vault settings</h1>
        <p className="mt-2 text-sm text-stone-600 leading-relaxed">
          Each section has its <b>own passcode</b> now. Change, reset, or lock any of them independently. Owner-only.
        </p>
      </header>
      <VaultSectionList />
    </div>
  );
}

// Reusable body — also rendered as a card inside /dashboard/settings so the
// owner can manage vault passcodes from the same Center Settings page.
export function VaultSectionList() {
  const [sections, setSections] = useState(null);

  const reload = () => {
    api.get("/vault/passcode/sections/status")
      .then((r) => setSections(r.data?.sections || []))
      .catch(() => setSections([]));
  };
  useEffect(() => { reload(); }, []);

  return (
    <>
      {sections === null && (
        <div className="py-10 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-emerald-700" /></div>
      )}
      {sections && sections.length === 0 && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900" data-testid="vault-settings-empty">
          Couldn&apos;t load section statuses. Refresh the page to try again.
        </div>
      )}
      {sections && sections.length > 0 && (
        <div className="space-y-4">
          {sections.map((s) => (
            <SectionRow key={s.section} sec={s} onUpdate={reload} />
          ))}
        </div>
      )}
    </>
  );
}

function SectionRow({ sec, onUpdate }) {
  const [open, setOpen] = useState(false);
  const Icon = SECTION_ICONS[sec.section] || KeyRound;
  return (
    <div data-testid={`vault-section-${sec.section}`} className="rounded-2xl border border-stone-200 bg-white overflow-hidden">
      <button type="button" onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-4 p-5 hover:bg-stone-50 text-left"
        data-testid={`vault-section-toggle-${sec.section}`}>
        <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-700 grid place-items-center shrink-0">
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-lg font-medium text-stone-900">{sec.label}</div>
          <div className="text-xs text-stone-500 mt-0.5">
            {sec.is_set
              ? "Passcode is set · own change / reset / lock controls below"
              : "No passcode yet — set one the first time you open this section"}
          </div>
        </div>
        <span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full ${sec.is_set ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
          {sec.is_set ? "set" : "not set"}
        </span>
        {open ? <ChevronDown className="w-4 h-4 text-stone-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-stone-400 shrink-0" />}
      </button>
      {open && (
        <div className="border-t border-stone-100 p-5 space-y-5 bg-stone-50/40">
          {sec.is_set ? (
            <>
              <ChangePasscode section={sec.section} label={sec.label} onUpdate={onUpdate} />
              <ForgotPasscode section={sec.section} label={sec.label} />
              <LockNow section={sec.section} label={sec.label} />
            </>
          ) : (
            <p className="text-xs text-stone-600">
              Open <b>{sec.label}</b> from the sidebar to set its passcode the first time. After that, you can change / reset / lock it from this page.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, testId, autoFocus }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[11px] uppercase tracking-widest font-semibold text-stone-700">{label}</Label>
      <Input type="password" inputMode="numeric" autoComplete="off" autoFocus={autoFocus}
        data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)}
        className="h-11 rounded-xl border-stone-300 focus-visible:ring-emerald-700 text-base tracking-widest" />
    </div>
  );
}

function ChangePasscode({ section, label, onUpdate }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (next.trim().length < 4) return toast.error("New passcode must be at least 4 characters.");
    if (next !== confirm) return toast.error("New passcode and confirmation don't match.");
    if (next === current) return toast.error("New passcode must be different from the current one.");
    setBusy(true);
    try {
      const r = await api.post("/vault/passcode/change", { current_passcode: current, new_passcode: next, section });
      if (r.data?.token) localStorage.setItem(`cc_vault_token__${section}`, r.data.token);
      setCurrent(""); setNext(""); setConfirm("");
      toast.success(`${label} passcode updated.`);
      onUpdate && onUpdate();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} data-testid={`vault-change-${section}`} className="space-y-3 bg-white rounded-xl p-4 border border-stone-200">
      <div className="text-xs font-semibold text-stone-700 inline-flex items-center gap-1.5"><KeyRound className="w-3.5 h-3.5" /> Change passcode</div>
      <Field label="Current passcode" value={current} onChange={setCurrent} testId={`vault-change-${section}-current`} />
      <Field label="New passcode" value={next} onChange={setNext} testId={`vault-change-${section}-new`} />
      <Field label="Confirm new passcode" value={confirm} onChange={setConfirm} testId={`vault-change-${section}-confirm`} />
      <Button type="submit" disabled={busy || !current || !next || !confirm} data-testid={`vault-change-${section}-submit`}
        className="w-full bg-emerald-700 hover:bg-emerald-800 disabled:opacity-40 text-white rounded-xl h-10 text-sm">
        {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Updating…</>
              : <><ShieldCheck className="w-4 h-4 mr-2" /> Update {label} passcode</>}
      </Button>
    </form>
  );
}

function ForgotPasscode({ section, label }) {
  const [busy, setBusy] = useState(false);
  const [sentTo, setSentTo] = useState("");
  const send = async () => {
    setBusy(true);
    try {
      const r = await api.post("/vault/passcode/forgot", { section });
      setSentTo(r.data?.sent_to || "");
      toast.success(`${label} reset link emailed.`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  return (
    <div className="bg-white rounded-xl p-4 border border-stone-200 space-y-2">
      <div className="text-xs font-semibold text-stone-700 inline-flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> Forgot {label} passcode?</div>
      <Button onClick={send} disabled={busy} data-testid={`vault-forgot-${section}-send`}
        className="w-full bg-amber-600 hover:bg-amber-700 disabled:opacity-40 text-white rounded-xl h-10 text-sm">
        {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</>
              : <><Mail className="w-4 h-4 mr-2" /> Email me a {label} reset link</>}
      </Button>
      {sentTo && (
        <div className="text-[11px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md px-2 py-1.5">
          ✓ Sent to <b>{sentTo}</b>. Open it within 15 minutes.
        </div>
      )}
    </div>
  );
}

function LockNow({ section, label }) {
  const [busy, setBusy] = useState(false);
  const lock = async () => {
    setBusy(true);
    try {
      await api.post("/vault/passcode/lock", null, { params: { section } });
      localStorage.removeItem(`cc_vault_token__${section}`);
      sessionStorage.removeItem(`cc_vault_token__${section}`);
      toast.success(`${label} locked. Re-enter passcode to access.`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  return (
    <div className="bg-white rounded-xl p-4 border border-stone-200 space-y-2">
      <div className="text-xs font-semibold text-stone-700 inline-flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" /> Lock {label} now</div>
      <Button onClick={lock} disabled={busy} variant="outline" data-testid={`vault-lock-${section}`}
        className="w-full rounded-xl h-10 text-sm border-stone-300 hover:bg-stone-50">
        {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Locking…</>
              : <><Lock className="w-4 h-4 mr-2" /> Lock {label} now</>}
      </Button>
    </div>
  );
}
